import React from 'react';
import { colors, availableEquipment } from './constants';
import { XIcon, EditIcon } from './Icons';

const EditBedModal = ({ 
  show, 
  onClose, 
  editBedData, 
  handleEditBedSubmit, 
  handleEditBedDataChange, 
  handleEquipmentToggle 
}) => {
  if (!show) return null;

  const styles = {
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    },
    modal: {
      background: colors.cream100,
      borderRadius: '12px',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '85vh',
      overflow: 'auto',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
    },
    modalHeader: {
      padding: '24px',
      borderBottom: `1px solid rgba(94, 82, 64, 0.12)`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      background: colors.cream100,
      zIndex: 1
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: 550,
      color: colors.slate900,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      color: colors.slate500,
      borderRadius: '6px',
      transition: 'all 0.2s ease'
    },
    modalBody: {
      padding: '24px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginBottom: '16px'
    },
    formLabel: {
      fontSize: '12px',
      fontWeight: 500,
      color: colors.slate900
    },
    formInput: {
      padding: '10px 12px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '8px',
      background: colors.cream50,
      color: colors.slate900,
      transition: 'all 0.2s ease',
      outline: 'none'
    },
    formTextarea: {
      padding: '10px 12px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '8px',
      background: colors.cream50,
      color: colors.slate900,
      transition: 'all 0.2s ease',
      outline: 'none',
      resize: 'vertical',
      minHeight: '80px'
    },
    equipmentGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
      marginTop: '8px'
    },
    equipmentChip: {
      padding: '8px 12px',
      fontSize: '13px',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '6px',
      background: colors.cream50,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textAlign: 'center'
    },
    equipmentChipActive: {
      background: colors.teal500,
      color: colors.cream50,
      borderColor: colors.teal500
    },
    modalFooter: {
      padding: '24px',
      borderTop: `1px solid rgba(94, 82, 64, 0.12)`,
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      position: 'sticky',
      bottom: 0,
      background: colors.cream100
    },
    btnPrimary: {
      padding: '12px 24px',
      fontSize: '14px',
      fontFamily: 'inherit',
      fontWeight: 500,
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: colors.teal500,
      color: colors.cream50
    },
    btnSecondary: {
      padding: '12px 24px',
      fontSize: '14px',
      fontFamily: 'inherit',
      fontWeight: 500,
      borderRadius: '8px',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: 'transparent',
      color: colors.slate900
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <EditIcon />
            Edit Bed Configuration
          </h2>
          <button 
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(94, 82, 64, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            <XIcon />
          </button>
        </div>

        <form onSubmit={handleEditBedSubmit}>
          <div style={styles.modalBody}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Bed ID</label>
              <input
                type="text"
                value={editBedData.id}
                style={styles.formInput}
                disabled
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Status</label>
              <select
                name="status"
                value={editBedData.status}
                onChange={handleEditBedDataChange}
                style={styles.formInput}
                onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
              >
                <option value="available">Available</option>
                <option value="occupied">Occupied</option>
                <option value="reserved">Reserved</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Bed Type</label>
              <select
                name="type"
                value={editBedData.type}
                onChange={handleEditBedDataChange}
                style={styles.formInput}
                onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
              >
                <option value="normal">Normal Ward</option>
                <option value="premium">Premium Ward</option>
                <option value="semi_premium">Semi-Premium Ward</option>
                <option value="icu">ICU Ward</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Floor</label>
              <input
                type="text"
                name="floor"
                value={editBedData.floor}
                onChange={handleEditBedDataChange}
                style={styles.formInput}
                onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Equipment</label>
              <div style={styles.equipmentGrid}>
                {availableEquipment.map(equipment => (
                  <div
                    key={equipment}
                    style={{
                      ...styles.equipmentChip,
                      ...(editBedData.equipment?.includes(equipment) ? styles.equipmentChipActive : {})
                    }}
                    onClick={() => handleEquipmentToggle(equipment)}
                    onMouseEnter={(e) => {
                      if (!editBedData.equipment?.includes(equipment)) {
                        e.currentTarget.style.background = 'rgba(94, 82, 64, 0.08)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!editBedData.equipment?.includes(equipment)) {
                        e.currentTarget.style.background = colors.cream50;
                      }
                    }}
                  >
                    {equipment}
                  </div>
                ))}
              </div>
            </div>

            {editBedData.status === 'maintenance' && (
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Maintenance Schedule</label>
                <input
                  type="date"
                  name="maintenanceSchedule"
                  value={editBedData.maintenanceSchedule}
                  onChange={handleEditBedDataChange}
                  style={styles.formInput}
                  onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                />
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Notes</label>
              <textarea
                name="notes"
                value={editBedData.notes}
                onChange={handleEditBedDataChange}
                style={styles.formTextarea}
                placeholder="Add any notes or special instructions..."
                onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
              />
            </div>
          </div>

          <div style={styles.modalFooter}>
            <button 
              type="button" 
              style={styles.btnSecondary}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(94, 82, 64, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              style={styles.btnPrimary}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.teal600;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(33, 128, 141, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = colors.teal500;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBedModal;
